import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  User,
  Users,
  Search,
  X,
  Loader2,
  Database,
  ShieldAlert,
  ShieldCheck,
  Server,
  Layers,
  UserX,
  UserCheck,
  Info,
  Tag,
  HardDrive,
  Key,
  Settings2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Copy,
  Check,
  Lock,
  Calendar,
} from 'lucide-react';
import baseapi from '../../../services/baseapi';
import { DBExportModal, type ColumnItem } from './DBExportModal';

interface CustomSelectOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

interface CustomSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: CustomSelectOption[];
  icon?: React.ReactNode;
  minWidth?: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  value,
  onChange,
  options,
  icon,
  minWidth = '135px',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const updateCoords = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, 150),
      });
    }
  }, []);

  const toggleOpen = () => {
    if (!isOpen) {
      updateCoords();
    }
    setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    const handleScrollOrResize = () => {
      if (isOpen) {
        updateCoords();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('resize', handleScrollOrResize);
      window.addEventListener('scroll', handleScrollOrResize, true);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleScrollOrResize);
      window.removeEventListener('scroll', handleScrollOrResize, true);
    };
  }, [isOpen, updateCoords]);

  const selectedOption = options.find((opt) => opt.value === value) || options[0];

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={toggleOpen}
        style={{ minWidth }}
        className="flex items-center justify-between gap-2 bg-white dark:bg-gray-800/90 hover:bg-gray-50 dark:hover:bg-gray-700/80 border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-gray-800 dark:text-gray-200 font-medium focus:outline-none focus:border-orange-500 transition-colors cursor-pointer shadow-sm h-8"
      >
        <div className="flex items-center gap-1.5 truncate min-w-0">
          {selectedOption?.icon || icon}
          <span className="truncate">{selectedOption?.label || value}</span>
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-gray-400 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: 'absolute',
              top: `${coords.top}px`,
              left: `${coords.left}px`,
              minWidth: `${coords.width}px`,
              zIndex: 99999,
            }}
            className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-2xl overflow-hidden py-1 max-h-60 overflow-y-auto custom-scroll animate-in fade-in slide-in-from-top-1 duration-150"
          >
            {options.map((opt) => {
              const isSelected = opt.value === value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    onChange(opt.value);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left px-3 py-1.5 text-xs font-semibold flex items-center justify-between gap-2 cursor-pointer transition-colors ${isSelected
                    ? 'bg-orange-50 dark:bg-orange-500/15 text-orange-600 dark:text-orange-400 font-bold'
                    : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                    }`}
                >
                  <div className="flex items-center gap-2 truncate min-w-0">
                    {opt.icon}
                    <span className="truncate">{opt.label}</span>
                  </div>
                  {isSelected && <Check className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400 shrink-0" />}
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </>
  );
};


interface UsersLoginsViewProps {
  activeCluster: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
}

export interface PrincipalItem {
  account_status?: string;
  agent_id?: number | string;
  asset_id?: number | string;
  auth_type?: string;
  client_id?: number | string;
  cluster_id?: string;
  collected_at?: string;
  config_id?: string;
  created_at?: string;
  database?: string;
  db_type?: string;
  default_database?: string;
  hostname?: string;
  indexed_at?: string;
  ip?: string;
  is_disabled?: boolean;
  is_system?: boolean;
  last_login?: string;
  log_timestamp?: string;
  login_name?: string;
  master_agent_id?: string;
  modified_at?: string;
  name: string;
  organization_id?: number | string;
  os?: string;
  profile?: string;
  roles?: string;
  scope?: string;
  type?: string;
}

export interface UsersSummary {
  total_principals?: number;
  total_databases?: number;
  databases?: string[];
  server_scope_count?: number;
  database_scope_count?: number;
  system_count?: number;
  disabled_count?: number;
}

export const UsersLoginsView: React.FC<UsersLoginsViewProps> = ({
  activeCluster,
  clientId,
  refreshKey,
  dbType,
}) => {
  const isMongoDB = ['mongodb', 'mongo'].includes((dbType || '').toLowerCase());
  const isOracle = ['oracle'].includes((dbType || '').toLowerCase()) || activeCluster.toLowerCase().includes('oracle');
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<PrincipalItem[]>([]);
  const [summary, setSummary] = useState<UsersSummary | null>(null);

  // Filters & Search
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedScope, setSelectedScope] = useState<string>('all');
  const [selectedDatabase, setSelectedDatabase] = useState<string>('all');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [originFilter, setOriginFilter] = useState<'all' | 'system' | 'custom'>('all');

  // Sorting
  const [sortBy, setSortBy] = useState<keyof PrincipalItem>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Pagination
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(25);

  // Selected item for drawer
  const [selectedPrincipal, setSelectedPrincipal] = useState<PrincipalItem | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);

    const params: any = {
      cluster_id: activeCluster,
      
    };
    if (clientId) params.client_id = clientId;

    try {
      const res = await baseapi.get('/db_monitor_dashboard/users', { params });
      if (res.data?.status || res.data?.data) {
        const rawList = res.data?.data || (Array.isArray(res.data) ? res.data : []);
        const list: PrincipalItem[] = Array.isArray(rawList) ? rawList : [];
        setData(list);
        setSummary(res.data?.summary || null);
      } else {
        setData([]);
        setSummary(null);
      }
    } catch (err) {
      console.error('UsersLoginsView fetch error:', err);
      setData([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [activeCluster, clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  // Handle Copy
  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1800);
  };

  // Computed summary values fallback
  const calculatedSummary = useMemo(() => {
    const totalPrincipals = summary?.total_principals ?? data.length;
    const allDbs = new Set<string>();
    let serverScope = 0;
    let dbScope = 0;
    let system = 0;
    let disabled = 0;

    data.forEach((p) => {
      if (p.database && p.database !== 'ALL') allDbs.add(p.database);
      if (p.scope?.toUpperCase() === 'SERVER') serverScope++;
      else if (p.scope?.toUpperCase() === 'DATABASE') dbScope++;

      if (p.is_system) system++;
      if (p.is_disabled) disabled++;
    });

    const dbsList = summary?.databases && summary.databases.length > 0
      ? summary.databases
      : Array.from(allDbs).sort();

    return {
      total_principals: summary?.total_principals ?? totalPrincipals,
      total_databases: summary?.total_databases ?? dbsList.length,
      databases: dbsList,
      server_scope_count: summary?.server_scope_count ?? serverScope,
      database_scope_count: summary?.database_scope_count ?? dbScope,
      system_count: summary?.system_count ?? system,
      disabled_count: summary?.disabled_count ?? disabled,
    };
  }, [summary, data]);

  // Unique list of types and scopes for dropdowns
  const availableTypes = useMemo(() => {
    const types = new Set<string>();
    data.forEach((p) => {
      if (p.type) types.add(p.type);
    });
    return Array.from(types).sort();
  }, [data]);

  // Database counts mapping from loaded records
  const databasePrincipalCounts = useMemo(() => {
    const map: Record<string, number> = {};
    calculatedSummary.databases.forEach((db) => {
      map[db] = 0;
    });
    data.forEach((p) => {
      if (p.database && p.database !== 'ALL') {
        map[p.database] = (map[p.database] || 0) + 1;
      }
    });
    return map;
  }, [calculatedSummary.databases, data]);

  // Distribution breakdowns
  const statusDistribution = useMemo(() => {
    const counts: Record<string, number> = {};
    data.forEach((p) => {
      const statusKey = (p.account_status || (p.is_disabled ? 'LOCKED' : 'OPEN')).toUpperCase();
      counts[statusKey] = (counts[statusKey] || 0) + 1;
    });

    return Object.entries(counts)
      .map(([status, count]) => ({
        status,
        count,
        percentage: data.length > 0 ? (count / data.length) * 100 : 0,
      }))
      .sort((a, b) => b.count - a.count);
  }, [data]);

  const typeDistribution = useMemo(() => {
    const counts: Record<string, number> = {};
    data.forEach((p) => {
      const typeKey = p.type || 'UNKNOWN';
      counts[typeKey] = (counts[typeKey] || 0) + 1;
    });

    return Object.entries(counts)
      .map(([type, count]) => ({
        type,
        count,
        percentage: data.length > 0 ? (count / data.length) * 100 : 0,
      }))
      .sort((a, b) => b.count - a.count);
  }, [data]);

  const originDistribution = useMemo(() => {
    let system = 0;
    let custom = 0;
    data.forEach((p) => {
      if (p.is_system) system++;
      else custom++;
    });
    const total = data.length || 1;
    return {
      system,
      custom,
      systemPercent: (system / total) * 100,
      customPercent: (custom / total) * 100,
    };
  }, [data]);

  // Filtered & Sorted Data
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      // Search
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        const matchName = item.name?.toLowerCase().includes(query);
        const matchLogin = item.login_name?.toLowerCase().includes(query);
        const matchDb = item.database?.toLowerCase().includes(query);
        const matchType = item.type?.toLowerCase().includes(query);
        const matchRoles = item.roles?.toLowerCase().includes(query);
        const matchStatus = item.account_status?.toLowerCase().includes(query);
        const matchProfile = item.profile?.toLowerCase().includes(query);
        if (!matchName && !matchLogin && !matchDb && !matchType && !matchRoles && !matchStatus && !matchProfile) {
          return false;
        }
      }

      // Status (Oracle / Account status)
      if (selectedStatus !== 'all') {
        const itemStatus = (item.account_status || (item.is_disabled ? 'LOCKED' : 'OPEN')).toUpperCase();
        if (itemStatus !== selectedStatus.toUpperCase()) {
          return false;
        }
      }

      // Scope
      if (selectedScope !== 'all') {
        if ((item.scope || '').toUpperCase() !== selectedScope.toUpperCase()) {
          return false;
        }
      }

      // Database
      if (selectedDatabase !== 'all') {
        if (item.database !== selectedDatabase) {
          return false;
        }
      }

      // Type
      if (selectedType !== 'all') {
        if (item.type !== selectedType) {
          return false;
        }
      }

      // Origin
      if (originFilter === 'system' && !item.is_system) return false;
      if (originFilter === 'custom' && item.is_system) return false;

      return true;
    }).sort((a, b) => {
      let aVal: any = a[sortBy] ?? '';
      let bVal: any = b[sortBy] ?? '';

      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, searchQuery, selectedScope, selectedDatabase, selectedType, selectedStatus, originFilter, sortBy, sortOrder]);

  // Paginated Data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredData.slice(startIndex, startIndex + pageSize);
  }, [filteredData, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredData.length / pageSize) || 1;

  const exportColumns = useMemo<ColumnItem[]>(() => {
    if (isOracle) {
      return [
        { key: 'name', label: 'Name' },
        { key: 'account_status', label: 'Status' },
        { key: 'profile', label: 'Profile' },
        { key: 'is_system', label: 'Origin' },
        { key: 'created_at', label: 'Created At' },
        { key: 'last_login', label: 'Last Login' },
        { key: 'scope', label: 'Scope' },
        { key: 'roles', label: 'Assigned Roles' },
      ];
    }
    const cols: ColumnItem[] = [
      { key: 'name', label: 'Name' },
      { key: 'database', label: 'Database' },
      { key: 'scope', label: 'Scope' },
    ];
    if (!isMongoDB) {
      cols.push({ key: 'type', label: 'Type' });
    }
    cols.push({ key: 'is_system', label: 'Origin' });
    if (!isMongoDB) {
      cols.push({ key: 'default_database', label: 'Default Database' });
    }
    cols.push(
      { key: 'roles', label: 'Assigned Roles' },
      { key: 'created_at', label: 'Created At' },
      { key: 'last_login', label: 'Last Login' }
    );
    return cols;
  }, [isOracle, isMongoDB]);

  const exportExtraParams = useMemo(() => {
    const params: Record<string, any> = {};
    if (searchQuery.trim()) {
      params.search = searchQuery.trim();
    }
    if (selectedScope !== 'all') {
      params.scope = selectedScope;
    }
    if (selectedDatabase !== 'all') {
      params.database = selectedDatabase;
    }
    if (selectedType !== 'all') {
      params.type = selectedType;
    }
    if (selectedStatus !== 'all') {
      params.status = selectedStatus;
    }
    if (originFilter !== 'all') {
      params.origin = originFilter;
      params.is_system = originFilter === 'system' ? 'true' : 'false';
    }
    return params;
  }, [searchQuery, selectedScope, selectedDatabase, selectedType, selectedStatus, originFilter]);

  const handleSort = (field: keyof PrincipalItem) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const SortIcon = ({ field }: { field: keyof PrincipalItem }) => {
    if (sortBy !== field) {
      return <ArrowUpDown className="w-3 h-3 text-gray-400 dark:text-gray-200 opacity-60 ml-1 inline-block" />;
    }
    return sortOrder === 'asc' ? (
      <ArrowUp className="w-3 h-3 text-orange-500 dark:text-orange-400 ml-1 inline-block" />
    ) : (
      <ArrowDown className="w-3 h-3 text-orange-500 dark:text-orange-400 ml-1 inline-block" />
    );
  };

  // Helper for Type formatting
  const formatTypeName = (type?: string) => {
    if (!type) return '-';
    return type.replace(/_/g, ' ');
  };

  // Helper for Type Badge styling
  const getTypeBadgeStyle = (type?: string) => {
    switch (type) {
      case 'SQL_LOGIN':
        return 'text-emerald-600 dark:text-emerald-400';
      case 'SQL_USER':
        return 'text-blue-600 dark:text-blue-400';
      case 'DATABASE_ROLE':
        return 'text-purple-600 dark:text-purple-400';
      case 'WINDOWS_LOGIN':
      case 'WINDOWS_GROUP':
        return 'text-amber-600 dark:text-amber-400';
      case 'CERTIFICATE_MAPPED_USER':
        return 'text-cyan-600 dark:text-cyan-400';
      default:
        return 'text-gray-700 dark:text-gray-200';
    }
  };

  // Helper for Oracle Account Status Badge styling
  const getAccountStatusBadge = (status?: string, isDisabled?: boolean) => {
    const s = (status || (isDisabled ? 'LOCKED' : 'OPEN')).toUpperCase();
    if (s === 'OPEN') {
      return 'bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/30';
    }
    if (s.includes('LOCKED')) {
      return 'bg-rose-50 dark:bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-200 dark:border-rose-500/30';
    }
    if (s.includes('EXPIRED')) {
      return 'bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-500/30';
    }
    return 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700';
  };

  // Format timestamp helper
  const formatTimestamp = (ts?: string) => {
    if (!ts) return '—';
    try {
      const d = new Date(ts);
      if (isNaN(d.getTime())) return ts;
      return d.toISOString().replace('T', ' ').replace('Z', ' UTC').substring(0, 19) + ' UTC';
    } catch {
      return ts;
    }
  };

  // Custom Dropdown Options
  const availableStatuses = useMemo(() => {
    const set = new Set<string>();
    data.forEach((p) => {
      if (p.account_status) {
        set.add(p.account_status);
      } else if (p.is_disabled !== undefined) {
        set.add(p.is_disabled ? 'LOCKED' : 'OPEN');
      }
    });
    return Array.from(set).sort();
  }, [data]);

  const statusOptions: CustomSelectOption[] = useMemo(() => {
    const list: CustomSelectOption[] = [
      { value: 'all', label: 'All Statuses', icon: <UserCheck className="w-3.5 h-3.5 text-gray-400" /> },
    ];
    availableStatuses.forEach((st) => {
      list.push({
        value: st,
        label: st,
        icon: st.toUpperCase() === 'OPEN'
          ? <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
          : <Lock className="w-3.5 h-3.5 text-rose-500 shrink-0" />,
      });
    });
    return list;
  }, [availableStatuses]);

  const scopeOptions: CustomSelectOption[] = useMemo(() => [
    { value: 'all', label: 'All Scopes', icon: <Layers className="w-3.5 h-3.5 text-gray-400" /> },
    { value: 'SERVER', label: 'Server', icon: <Server className="w-3.5 h-3.5 text-purple-500 dark:text-purple-400" /> },
    { value: 'DATABASE', label: 'Database', icon: <Layers className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" /> },
  ], []);

  const databaseOptions: CustomSelectOption[] = useMemo(() => {
    const list: CustomSelectOption[] = [
      { value: 'all', label: 'All Databases', icon: <Database className="w-3.5 h-3.5 text-gray-400" /> },
    ];
    calculatedSummary.databases.forEach((db) => {
      list.push({
        value: db,
        label: db,
        icon: <Database className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" />,
      });
    });
    if (!calculatedSummary.databases.includes('ALL')) {
      list.push({
        value: 'ALL',
        label: 'ALL',
        icon: <Database className="w-3.5 h-3.5 text-purple-500 dark:text-purple-400" />,
      });
    }
    return list;
  }, [calculatedSummary.databases]);

  const typeOptions: CustomSelectOption[] = useMemo(() => {
    const list: CustomSelectOption[] = [
      { value: 'all', label: 'All Types', icon: <Tag className="w-3.5 h-3.5 text-gray-400" /> },
    ];
    availableTypes.forEach((type) => {
      list.push({
        value: type,
        label: formatTypeName(type),
        icon: <Tag className="w-3.5 h-3.5 text-cyan-500 dark:text-cyan-400" />,
      });
    });
    return list;
  }, [availableTypes]);

  return (
    <div className="space-y-5 animate-in fade-in duration-200 overflow-x-hidden">
      {loading && !data.length ? (
        <div className="flex flex-col items-center justify-center py-24 bg-white/60 dark:bg-gray-900/40 rounded-xl border border-gray-200 dark:border-gray-800">
          <Loader2 className="w-8 h-8 text-orange-500 animate-spin mb-3" />
          <p className="text-sm font-medium text-gray-800 dark:text-gray-200">Loading security principals...</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Scanning server logins, database users, and roles</p>
        </div>
      ) : (
        <>
          {/* Top 8 KPI Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-2.5">
            {/* Total Principals */}
            <div className="bg-white dark:bg-gray-900/80 rounded-xl border border-gray-200 dark:border-gray-800/80 p-3 shadow-sm hover:border-gray-300 dark:hover:border-gray-700 transition-colors flex flex-col justify-between">
              <div className="flex items-center justify-between gap-1 text-gray-600 dark:text-gray-400">
                <div className="flex items-center gap-1.5 truncate min-w-0">
                  <Users className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400 shrink-0" />
                  <span className="text-[10px] font-bold uppercase tracking-wider truncate">
                    Total
                  </span>
                </div>
                <div className="group/info relative inline-flex items-center">
                  <Info className="w-3 h-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 cursor-help shrink-0 transition-colors" />
                  <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1.5 z-50 px-2 py-1 bg-gray-900/95 dark:bg-gray-950/95 backdrop-blur-sm text-gray-100 dark:text-gray-200 text-[10px] font-medium rounded-md border border-gray-700 shadow-2xl whitespace-nowrap opacity-0 invisible group-hover/info:opacity-100 group-hover/info:visible transition-all duration-150 pointer-events-none">
                    All discovered principals
                  </div>
                </div>
              </div>
              <div className="mt-2">
                <span className="text-xl font-black text-gray-900 dark:text-white tracking-tight">
                  {calculatedSummary.total_principals}
                </span>
              </div>
            </div>

            {/* Databases */}
            <div className="bg-white dark:bg-gray-900/80 rounded-xl border border-gray-200 dark:border-gray-800/80 p-3 shadow-sm hover:border-gray-300 dark:hover:border-gray-700 transition-colors flex flex-col justify-between">
              <div className="flex items-center justify-between gap-1 text-gray-600 dark:text-gray-400">
                <div className="flex items-center gap-1.5 truncate min-w-0">
                  <Database className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400 shrink-0" />
                  <span className="text-[10px] font-bold uppercase tracking-wider truncate">
                    Databases
                  </span>
                </div>
                <div className="group/info relative inline-flex items-center">
                  <Info className="w-3 h-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 cursor-help shrink-0 transition-colors" />
                  <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1.5 z-50 px-2 py-1 bg-gray-900/95 dark:bg-gray-950/95 backdrop-blur-sm text-gray-100 dark:text-gray-200 text-[10px] font-medium rounded-md border border-gray-700 shadow-2xl whitespace-nowrap opacity-0 invisible group-hover/info:opacity-100 group-hover/info:visible transition-all duration-150 pointer-events-none">
                    Databases scanned
                  </div>
                </div>
              </div>
              <div className="mt-2">
                <span className="text-xl font-black text-gray-900 dark:text-white tracking-tight">
                  {calculatedSummary.total_databases}
                </span>
              </div>
            </div>

            {/* Server Scope */}
            <div
              onClick={() => {
                setSelectedScope(selectedScope === 'SERVER' ? 'all' : 'SERVER');
                setCurrentPage(1);
              }}
              className={`rounded-xl border p-3 shadow-sm transition-all cursor-pointer flex flex-col justify-between ${selectedScope === 'SERVER'
                ? 'bg-purple-50/70 dark:bg-purple-950/30 border-purple-300 dark:border-purple-500 ring-1 ring-purple-400/50 dark:ring-purple-500/50'
                : 'bg-white dark:bg-gray-900/80 border-gray-200 dark:border-gray-800/80 hover:border-purple-300 dark:hover:border-purple-500/50'
                }`}
            >
              <div className="flex items-center justify-between gap-1 text-gray-600 dark:text-gray-400">
                <div className="flex items-center gap-1.5 truncate min-w-0">
                  <Server className="w-3.5 h-3.5 text-purple-500 dark:text-purple-400 shrink-0" />
                  <span className="text-[10px] font-bold uppercase tracking-wider truncate">
                    Server Scope
                  </span>
                </div>
                <div className="group/info relative inline-flex items-center">
                  <Info className="w-3 h-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 cursor-help shrink-0 transition-colors" />
                  <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1.5 z-50 px-2 py-1 bg-gray-900/95 dark:bg-gray-950/95 backdrop-blur-sm text-gray-100 dark:text-gray-200 text-[10px] font-medium rounded-md border border-gray-700 shadow-2xl whitespace-nowrap opacity-0 invisible group-hover/info:opacity-100 group-hover/info:visible transition-all duration-150 pointer-events-none">
                    Server-level logins (SQL_LOGIN)
                  </div>
                </div>
              </div>
              <div className="mt-2">
                <span className="text-xl font-black text-purple-600 dark:text-purple-400 tracking-tight">
                  {calculatedSummary.server_scope_count}
                </span>
              </div>
            </div>

            {/* Database Scope */}
            <div
              onClick={() => {
                setSelectedScope(selectedScope === 'DATABASE' ? 'all' : 'DATABASE');
                setCurrentPage(1);
              }}
              className={`rounded-xl border p-3 shadow-sm transition-all cursor-pointer flex flex-col justify-between ${selectedScope === 'DATABASE'
                ? 'bg-blue-50/70 dark:bg-blue-950/30 border-blue-300 dark:border-blue-500 ring-1 ring-blue-400/50 dark:ring-blue-500/50'
                : 'bg-white dark:bg-gray-900/80 border-gray-200 dark:border-gray-800/80 hover:border-blue-300 dark:hover:border-blue-500/50'
                }`}
            >
              <div className="flex items-center justify-between gap-1 text-gray-600 dark:text-gray-400">
                <div className="flex items-center gap-1.5 truncate min-w-0">
                  <Layers className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400 shrink-0" />
                  <span className="text-[10px] font-bold uppercase tracking-wider truncate">
                    Database Scope
                  </span>
                </div>
                <div className="group/info relative inline-flex items-center">
                  <Info className="w-3 h-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 cursor-help shrink-0 transition-colors" />
                  <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1.5 z-50 px-2 py-1 bg-gray-900/95 dark:bg-gray-950/95 backdrop-blur-sm text-gray-100 dark:text-gray-200 text-[10px] font-medium rounded-md border border-gray-700 shadow-2xl whitespace-nowrap opacity-0 invisible group-hover/info:opacity-100 group-hover/info:visible transition-all duration-150 pointer-events-none">
                    Database users and roles
                  </div>
                </div>
              </div>
              <div className="mt-2">
                <span className="text-xl font-black text-blue-600 dark:text-blue-400 tracking-tight">
                  {calculatedSummary.database_scope_count}
                </span>
              </div>
            </div>

            {/* System */}
            <div
              onClick={() => {
                setOriginFilter(originFilter === 'system' ? 'all' : 'system');
                setCurrentPage(1);
              }}
              className={`rounded-xl border p-3 shadow-sm transition-all cursor-pointer flex flex-col justify-between ${originFilter === 'system'
                ? 'bg-amber-50/70 dark:bg-amber-950/30 border-amber-300 dark:border-amber-500 ring-1 ring-amber-400/50 dark:ring-amber-500/50'
                : 'bg-white dark:bg-gray-900/80 border-gray-200 dark:border-gray-800/80 hover:border-amber-300 dark:hover:border-amber-500/50'
                }`}
            >
              <div className="flex items-center justify-between gap-1 text-gray-600 dark:text-gray-400">
                <div className="flex items-center gap-1.5 truncate min-w-0">
                  <ShieldAlert className="w-3.5 h-3.5 text-amber-500 dark:text-amber-400 shrink-0" />
                  <span className="text-[10px] font-bold uppercase tracking-wider truncate">
                    System
                  </span>
                </div>
                <div className="group/info relative inline-flex items-center">
                  <Info className="w-3 h-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 cursor-help shrink-0 transition-colors" />
                  <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1.5 z-50 px-2 py-1 bg-gray-900/95 dark:bg-gray-950/95 backdrop-blur-sm text-gray-100 dark:text-gray-200 text-[10px] font-medium rounded-md border border-gray-700 shadow-2xl whitespace-nowrap opacity-0 invisible group-hover/info:opacity-100 group-hover/info:visible transition-all duration-150 pointer-events-none">
                    System principals
                  </div>
                </div>
              </div>
              <div className="mt-2">
                <span className="text-xl font-black text-amber-600 dark:text-amber-400 tracking-tight">
                  {calculatedSummary.system_count}
                </span>
              </div>
            </div>

            {/* Custom */}
            <div
              onClick={() => {
                setOriginFilter(originFilter === 'custom' ? 'all' : 'custom');
                setCurrentPage(1);
              }}
              className={`rounded-xl border p-3 shadow-sm transition-all cursor-pointer flex flex-col justify-between ${originFilter === 'custom'
                ? 'bg-emerald-50/70 dark:bg-emerald-950/30 border-emerald-300 dark:border-emerald-500 ring-1 ring-emerald-400/50 dark:ring-emerald-500/50'
                : 'bg-white dark:bg-gray-900/80 border-gray-200 dark:border-gray-800/80 hover:border-emerald-300 dark:hover:border-emerald-500/50'
                }`}
            >
              <div className="flex items-center justify-between gap-1 text-gray-600 dark:text-gray-400">
                <div className="flex items-center gap-1.5 truncate min-w-0">
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400 shrink-0" />
                  <span className="text-[10px] font-bold uppercase tracking-wider truncate">
                    Custom
                  </span>
                </div>
                <div className="group/info relative inline-flex items-center">
                  <Info className="w-3 h-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 cursor-help shrink-0 transition-colors" />
                  <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1.5 z-50 px-2 py-1 bg-gray-900/95 dark:bg-gray-950/95 backdrop-blur-sm text-gray-100 dark:text-gray-200 text-[10px] font-medium rounded-md border border-gray-700 shadow-2xl whitespace-nowrap opacity-0 invisible group-hover/info:opacity-100 group-hover/info:visible transition-all duration-150 pointer-events-none">
                    User-created principals
                  </div>
                </div>
              </div>
              <div className="mt-2">
                <span className="text-xl font-black text-emerald-600 dark:text-emerald-400 tracking-tight">
                  {originDistribution.custom}
                </span>
              </div>
            </div>

            {/* Active */}
            <div className="bg-white dark:bg-gray-900/80 rounded-xl border border-gray-200 dark:border-gray-800/80 p-3 shadow-sm hover:border-gray-300 dark:hover:border-gray-700 transition-colors flex flex-col justify-between">
              <div className="flex items-center justify-between gap-1 text-gray-600 dark:text-gray-400">
                <div className="flex items-center gap-1.5 truncate min-w-0">
                  <UserCheck className="w-3.5 h-3.5 text-cyan-500 dark:text-cyan-400 shrink-0" />
                  <span className="text-[10px] font-bold uppercase tracking-wider truncate">
                    Active
                  </span>
                </div>
                <div className="group/info relative inline-flex items-center">
                  <Info className="w-3 h-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 cursor-help shrink-0 transition-colors" />
                  <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1.5 z-50 px-2 py-1 bg-gray-900/95 dark:bg-gray-950/95 backdrop-blur-sm text-gray-100 dark:text-gray-200 text-[10px] font-medium rounded-md border border-gray-700 shadow-2xl whitespace-nowrap opacity-0 invisible group-hover/info:opacity-100 group-hover/info:visible transition-all duration-150 pointer-events-none">
                    Active enabled accounts
                  </div>
                </div>
              </div>
              <div className="mt-2">
                <span className="text-xl font-black text-cyan-600 dark:text-cyan-400 tracking-tight">
                  {Math.max(0, (calculatedSummary.total_principals ?? 0) - (calculatedSummary.disabled_count ?? 0))}
                </span>
              </div>
            </div>

            {/* Disabled */}
            <div className="bg-white dark:bg-gray-900/80 rounded-xl border border-gray-200 dark:border-gray-800/80 p-3 shadow-sm hover:border-gray-300 dark:hover:border-gray-700 transition-colors flex flex-col justify-between">
              <div className="flex items-center justify-between gap-1 text-gray-600 dark:text-gray-400">
                <div className="flex items-center gap-1.5 truncate min-w-0">
                  <UserX className="w-3.5 h-3.5 text-red-500 dark:text-red-400 shrink-0" />
                  <span className="text-[10px] font-bold uppercase tracking-wider truncate">
                    Disabled
                  </span>
                </div>
                <div className="group/info relative inline-flex items-center">
                  <Info className="w-3 h-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 cursor-help shrink-0 transition-colors" />
                  <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1.5 z-50 px-2 py-1 bg-gray-900/95 dark:bg-gray-950/95 backdrop-blur-sm text-gray-100 dark:text-gray-200 text-[10px] font-medium rounded-md border border-gray-700 shadow-2xl whitespace-nowrap opacity-0 invisible group-hover/info:opacity-100 group-hover/info:visible transition-all duration-150 pointer-events-none">
                    Disabled accounts
                  </div>
                </div>
              </div>
              <div className="mt-2">
                <span className="text-xl font-black text-red-600 dark:text-red-400 tracking-tight">
                  {calculatedSummary.disabled_count}
                </span>
              </div>
            </div>
          </div>

          {/* Database Coverage Grid (Hidden for MongoDB) */}
          {!isMongoDB && (
            <div className="bg-white dark:bg-gray-900/80 rounded-xl border border-gray-200 dark:border-gray-800/80 p-4 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h2 className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider flex items-center gap-1.5">
                    <Database className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" />
                    Database Coverage
                  </h2>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">
                    Databases included in the security inventory
                  </p>
                </div>
                <span className="text-[11px] font-medium text-gray-600 dark:text-gray-400">
                  {calculatedSummary.databases.length} databases
                </span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2.5">
                {calculatedSummary.databases.map((dbName) => {
                  const count = databasePrincipalCounts[dbName] || 0;
                  const isSelected = selectedDatabase === dbName;

                  return (
                    <button
                      key={dbName}
                      onClick={() => {
                        setSelectedDatabase(isSelected ? 'all' : dbName);
                        setCurrentPage(1);
                      }}
                      className={`p-3 rounded-lg border text-left transition-all flex flex-col justify-between cursor-pointer ${isSelected
                        ? 'bg-orange-50 dark:bg-orange-500/10 border-orange-400 dark:border-orange-500/60 text-gray-900 dark:text-white ring-1 ring-orange-400/40 dark:ring-orange-500/40'
                        : 'bg-gray-50 dark:bg-gray-800/40 border-gray-200 dark:border-gray-700/60 hover:bg-gray-100 dark:hover:bg-gray-800/80 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                    >
                      <span className="text-xs font-bold text-gray-900 dark:text-white truncate max-w-full block" title={dbName}>
                        {dbName}
                      </span>
                      <div className="mt-2 flex items-baseline gap-1">
                        <span className={`text-lg font-black ${isSelected ? 'text-orange-500 dark:text-orange-400' : 'text-blue-600 dark:text-blue-400'}`}>
                          {count}
                        </span>
                        <span className="text-[10px] text-gray-500 dark:text-gray-400">principals</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Principal Inventory Main Table */}
          <div className="bg-white dark:bg-gray-900/80 rounded-xl border border-gray-200 dark:border-gray-800/80 shadow-sm overflow-hidden">
            {/* Table Header & Controls */}
            <div className="p-4 border-b border-gray-100 dark:border-gray-800 space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <h2 className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400" />
                     Roles & Privileges
                  </h2>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">
                    Server logins, database users and database roles
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                    {filteredData.length} {filteredData.length === 1 ? 'principal' : 'principals'}
                  </div>
                  <DBExportModal
                    endpoint="/db_monitor_dashboard/users"
                    columns={exportColumns}
                    cluster_id={activeCluster}
                    client_id={clientId}
                    extraParams={exportExtraParams}
                    defaultSortBy="name"
                    defaultSortOrder="asc"
                    buttonText="Export"
                    title="Export Roles & Privileges"
                    subtitle="Export database users, logins, roles and permissions"
                  />
                </div>
              </div>

              {/* Filter Bar */}
              <div className="flex flex-wrap items-center gap-2 pt-1">
                {/* Search input */}
                <div className="relative flex-1 min-w-[220px]">
                  <Search className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search name, login, database or type..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full bg-white dark:bg-gray-800/90 border border-gray-200 dark:border-gray-700 rounded-lg pl-8 pr-8 py-1.5 text-xs text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-white cursor-pointer"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                {/* Scope Filter */}
                <CustomSelect
                  value={selectedScope}
                  onChange={(val) => {
                    setSelectedScope(val);
                    setCurrentPage(1);
                  }}
                  options={scopeOptions}
                  minWidth="125px"
                />

                {/* Status Filter for Oracle */}
                {isOracle ? (
                  <CustomSelect
                    value={selectedStatus}
                    onChange={(val) => {
                      setSelectedStatus(val);
                      setCurrentPage(1);
                    }}
                    options={statusOptions}
                    minWidth="135px"
                  />
                ) : (
                  <>
                    {/* Database Filter */}
                    <CustomSelect
                      value={selectedDatabase}
                      onChange={(val) => {
                        setSelectedDatabase(val);
                        setCurrentPage(1);
                      }}
                      options={databaseOptions}
                      minWidth="140px"
                    />

                    {/* Type Filter */}
                    <CustomSelect
                      value={selectedType}
                      onChange={(val) => {
                        setSelectedType(val);
                        setCurrentPage(1);
                      }}
                      options={typeOptions}
                      minWidth="130px"
                    />
                  </>
                )}

                {/* Origin Segmented Toggle */}
                <div className="flex items-center bg-gray-100 dark:bg-gray-800 p-0.5 rounded-lg border border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => {
                      setOriginFilter('all');
                      setCurrentPage(1);
                    }}
                    className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all cursor-pointer ${originFilter === 'all'
                      ? 'bg-orange-500 text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                      }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => {
                      setOriginFilter('system');
                      setCurrentPage(1);
                    }}
                    className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all cursor-pointer ${originFilter === 'system'
                      ? 'bg-amber-600 text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                      }`}
                  >
                    System
                  </button>
                  <button
                    onClick={() => {
                      setOriginFilter('custom');
                      setCurrentPage(1);
                    }}
                    className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all cursor-pointer ${originFilter === 'custom'
                      ? 'bg-emerald-600 text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                      }`}
                  >
                    Custom
                  </button>
                </div>
              </div>
            </div>

            {/* Table Container */}
            <div className="overflow-x-auto custom-scroll">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 text-[11px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider select-none">
                    <th
                      onClick={() => handleSort('name')}
                      className="py-3 px-4 cursor-pointer hover:text-gray-900 dark:hover:text-white transition-colors"
                    >
                      <div className="flex items-center gap-1.5">
                        <User className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400/80" />
                        <span>NAME</span>
                        <SortIcon field="name" />
                      </div>
                    </th>
                    {isOracle ? (
                      <>
                        <th
                          onClick={() => handleSort('account_status')}
                          className="py-3 px-4 cursor-pointer hover:text-gray-900 dark:hover:text-white transition-colors"
                        >
                          <div className="flex items-center gap-1.5">
                            <Lock className="w-3.5 h-3.5 text-rose-500 dark:text-rose-400/80" />
                            <span>STATUS</span>
                            <SortIcon field="account_status" />
                          </div>
                        </th>
                        <th
                          onClick={() => handleSort('profile')}
                          className="py-3 px-4 cursor-pointer hover:text-gray-900 dark:hover:text-white transition-colors"
                        >
                          <div className="flex items-center gap-1.5">
                            <Tag className="w-3.5 h-3.5 text-cyan-500 dark:text-cyan-400/80" />
                            <span>PROFILE</span>
                            <SortIcon field="profile" />
                          </div>
                        </th>
                        <th
                          onClick={() => handleSort('is_system')}
                          className="py-3 px-4 cursor-pointer hover:text-gray-900 dark:hover:text-white transition-colors"
                        >
                          <div className="flex items-center gap-1.5">
                            <ShieldCheck className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400/80" />
                            <span>ORIGIN</span>
                            <SortIcon field="is_system" />
                          </div>
                        </th>
                        <th
                          onClick={() => handleSort('created_at')}
                          className="py-3 px-4 cursor-pointer hover:text-gray-900 dark:hover:text-white transition-colors"
                        >
                          <div className="flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400/80" />
                            <span>CREATED</span>
                            <SortIcon field="created_at" />
                          </div>
                        </th>
                        <th
                          onClick={() => handleSort('last_login')}
                          className="py-3 px-4 cursor-pointer hover:text-gray-900 dark:hover:text-white transition-colors"
                        >
                          <div className="flex items-center gap-1.5">
                            <UserCheck className="w-3.5 h-3.5 text-teal-500 dark:text-teal-400/80" />
                            <span>LAST LOGIN</span>
                            <SortIcon field="last_login" />
                          </div>
                        </th>
                        <th
                          onClick={() => handleSort('scope')}
                          className="py-3 px-4 cursor-pointer hover:text-gray-900 dark:hover:text-white transition-colors"
                        >
                          <div className="flex items-center gap-1.5">
                            <Server className="w-3.5 h-3.5 text-purple-500 dark:text-purple-400/80" />
                            <span>SCOPE</span>
                            <SortIcon field="scope" />
                          </div>
                        </th>
                      </>
                    ) : (
                      <>
                        <th
                          onClick={() => handleSort('database')}
                          className="py-3 px-4 cursor-pointer hover:text-gray-900 dark:hover:text-white transition-colors"
                        >
                          <div className="flex items-center gap-1.5">
                            <Database className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400/80" />
                            <span>DATABASE</span>
                            <SortIcon field="database" />
                          </div>
                        </th>
                        <th
                          onClick={() => handleSort('scope')}
                          className="py-3 px-4 cursor-pointer hover:text-gray-900 dark:hover:text-white transition-colors"
                        >
                          <div className="flex items-center gap-1.5">
                            <Server className="w-3.5 h-3.5 text-purple-500 dark:text-purple-400/80" />
                            <span>SCOPE</span>
                            <SortIcon field="scope" />
                          </div>
                        </th>
                        {!isMongoDB && (
                          <th
                            onClick={() => handleSort('type')}
                            className="py-3 px-4 cursor-pointer hover:text-gray-900 dark:hover:text-white transition-colors"
                          >
                            <div className="flex items-center gap-1.5">
                              <Tag className="w-3.5 h-3.5 text-cyan-500 dark:text-cyan-400/80" />
                              <span>TYPE</span>
                              <SortIcon field="type" />
                            </div>
                          </th>
                        )}
                        <th
                          onClick={() => handleSort('is_system')}
                          className="py-3 px-4 cursor-pointer hover:text-gray-900 dark:hover:text-white transition-colors"
                        >
                          <div className="flex items-center gap-1.5">
                            <ShieldCheck className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400/80" />
                            <span>ORIGIN</span>
                            <SortIcon field="is_system" />
                          </div>
                        </th>
                        {!isMongoDB && (
                          <th className="py-3 px-4">
                            <div className="flex items-center gap-1.5">
                              <HardDrive className="w-3.5 h-3.5 text-indigo-500 dark:text-indigo-400/80" />
                              <span>DEFAULT DB</span>
                            </div>
                          </th>
                        )}
                        <th className="py-3 px-4">
                          <div className="flex items-center gap-1.5">
                            <Key className="w-3.5 h-3.5 text-amber-500 dark:text-amber-400/80" />
                            <span>ROLES</span>
                          </div>
                        </th>
                      </>
                    )}
                    <th className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <Settings2 className="w-3.5 h-3.5 text-gray-400" />
                        <span>ACTION</span>
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-100 dark:divide-gray-800/60">
                  {paginatedData.length === 0 ? (
                    <tr>
                      <td colSpan={isOracle ? 8 : isMongoDB ? 6 : 8} className="py-12 text-center text-gray-500 dark:text-gray-400">
                        <Users className="w-8 h-8 text-gray-400 dark:text-gray-500 mx-auto mb-2 opacity-50" />
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-200">No principals match the filters</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Try resetting search or filters</p>
                      </td>
                    </tr>
                  ) : (
                    paginatedData.map((principal, idx) => {
                      const isSystem = principal.is_system;
                      const isDisabled = principal.is_disabled;
                      const scope = principal.scope?.toUpperCase() || 'SERVER';

                      return (
                        <tr
                          key={`${principal.name}-${principal.database}-${principal.type}-${idx}`}
                          className="hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors group"
                        >
                          {/* Name */}
                          <td className="py-3 px-4 font-semibold text-gray-900 dark:text-white">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-gray-900 dark:text-gray-100">{principal.name}</span>
                              {isDisabled && (
                                <span className="text-[10px] font-bold px-1.5 py-0.2 rounded bg-red-50 dark:bg-red-500/15 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-500/30">
                                  Disabled
                                </span>
                              )}
                            </div>
                            {principal.login_name && principal.login_name !== principal.name && (
                              <div className="text-[10px] text-gray-500 dark:text-gray-400 font-mono">
                                Login: {principal.login_name}
                              </div>
                            )}
                          </td>

                          {isOracle ? (
                            <>
                              {/* Account Status */}
                              <td className="py-3 px-4">
                                <span
                                  className={`text-[10px] font-bold px-2 py-0.5 rounded border ${getAccountStatusBadge(
                                    principal.account_status,
                                    principal.is_disabled
                                  )}`}
                                >
                                  {principal.account_status || (principal.is_disabled ? 'LOCKED' : 'OPEN')}
                                </span>
                              </td>

                              {/* Profile */}
                              <td className="py-3 px-4">
                                <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-mono border border-gray-200 dark:border-gray-700">
                                  {principal.profile || 'DEFAULT'}
                                </span>
                              </td>

                              {/* Origin */}
                              <td className="py-3 px-4">
                                <span
                                  className={`text-[10px] font-bold px-2 py-0.5 rounded border ${isSystem
                                    ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-500/30'
                                    : 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/30'
                                    }`}
                                >
                                  {isSystem ? 'System' : 'Custom'}
                                </span>
                              </td>

                              {/* Created At */}
                              <td className="py-3 px-4 font-mono text-[11px] text-gray-700 dark:text-gray-300">
                                {formatTimestamp(principal.created_at)}
                              </td>

                              {/* Last Login */}
                              <td className="py-3 px-4 font-mono text-[11px]">
                                {principal.last_login ? (
                                  <span className="text-gray-700 dark:text-gray-300 font-medium">
                                    {formatTimestamp(principal.last_login)}
                                  </span>
                                ) : (
                                  <span className="text-gray-400 dark:text-gray-500 italic">Never</span>
                                )}
                              </td>

                              {/* Scope */}
                              <td className="py-3 px-4">
                                <span className="text-[10px] font-bold px-2 py-0.5 rounded border bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-500/30">
                                  {scope}
                                </span>
                              </td>
                            </>
                          ) : (
                            <>
                              {/* Database */}
                              <td className="py-3 px-4">
                                <span
                                  className={`text-xs font-mono font-medium ${principal.database === 'ALL'
                                    ? 'text-purple-600 dark:text-purple-400'
                                    : 'text-blue-600 dark:text-blue-400 hover:underline cursor-pointer'
                                    }`}
                                  onClick={() => {
                                    if (principal.database && principal.database !== 'ALL') {
                                      setSelectedDatabase(principal.database);
                                      setCurrentPage(1);
                                    }
                                  }}
                                >
                                  {principal.database || '-'}
                                </span>
                              </td>

                              {/* Scope */}
                              <td className="py-3 px-4">
                                <span
                                  className={`text-[10px] font-bold px-2 py-0.5 rounded border ${scope === 'SERVER'
                                    ? 'bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-500/30'
                                    : 'bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-500/30'
                                    }`}
                                >
                                  {scope}
                                </span>
                              </td>

                              {/* Type */}
                              {!isMongoDB && (
                                <td className="py-3 px-4">
                                  <span
                                    className={`text-[10px] font-bold px-2 py-0.5 ${getTypeBadgeStyle(
                                      principal.type
                                    )}`}
                                  >
                                    {principal.type?.replace(/_/g, " ")}
                                  </span>
                                </td>
                              )}

                              {/* Origin */}
                              <td className="py-3 px-4">
                                <span
                                  className={`text-[10px] font-bold px-2 py-0.5 rounded border ${isSystem
                                    ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-500/30'
                                    : 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/30'
                                    }`}
                                >
                                  {isSystem ? 'System' : 'Custom'}
                                </span>
                              </td>

                              {/* Default DB */}
                              {!isMongoDB && (
                                <td className="py-3 px-4 font-mono text-gray-700 dark:text-gray-300">
                                  {principal.default_database || '-'}
                                </td>
                              )}

                              {/* Roles */}
                              <td className="py-3 px-4">
                                {principal.roles ? (
                                  <span
                                    className="text-gray-700 dark:text-gray-300 font-mono text-[11px] truncate max-w-[200px] block"
                                    title={principal.roles}
                                  >
                                    {principal.roles}
                                  </span>
                                ) : (
                                  <span className="text-gray-400 dark:text-gray-500">-</span>
                                )}
                              </td>
                            </>
                          )}

                          {/* Action */}
                          <td className="py-3 px-4 text-right">
                            <button
                              onClick={() => setSelectedPrincipal(principal)}
                              className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors inline-flex items-center gap-1 group-hover:underline cursor-pointer"
                            >
                              View &rarr;
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Table Footer: Pagination & Metadata */}
            <div className="p-3.5 border-t border-gray-100 dark:border-gray-800 bg-gray-50/70 dark:bg-gray-900/60 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-600 dark:text-gray-400">
              <div className="flex items-center gap-4">
                <span>
                  Showing{' '}
                  <strong className="text-gray-800 dark:text-gray-200">
                    {filteredData.length === 0 ? 0 : (currentPage - 1) * pageSize + 1}
                  </strong>{' '}
                  to{' '}
                  <strong className="text-gray-800 dark:text-gray-200">
                    {Math.min(currentPage * pageSize, filteredData.length)}
                  </strong>{' '}
                  of <strong className="text-gray-800 dark:text-gray-200">{filteredData.length}</strong> principals
                </span>

                {/* Page Size Selector */}
                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] text-gray-500 dark:text-gray-400">Per page:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded px-1.5 py-0.5 text-xs text-gray-700 dark:text-gray-200 font-medium focus:outline-none"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>

              {/* Pagination Controls */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-1 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed text-gray-700 dark:text-gray-200 cursor-pointer"
                  title="Previous Page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300 px-1">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-1 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed text-gray-700 dark:text-gray-200 cursor-pointer"
                  title="Next Page"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Bottom Analytics Cards: Principal Type Distribution & System vs Custom */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Principal Type Distribution (or Account Status for Oracle) */}
            <div className="bg-white dark:bg-gray-900/80 rounded-xl border border-gray-200 dark:border-gray-800/80 p-4 shadow-sm">
              <div className="mb-3">
                <h2 className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider flex items-center gap-1.5">
                  {isOracle ? (
                    <>
                      <Lock className="w-3.5 h-3.5 text-cyan-500 dark:text-cyan-400" />
                      Account Status Distribution
                    </>
                  ) : (
                    <>
                      <Tag className="w-3.5 h-3.5 text-cyan-500 dark:text-cyan-400" />
                      Principal Type Distribution
                    </>
                  )}
                </h2>
                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                  {isOracle ? 'Breakdown by user account lock status' : 'Breakdown from discovered records'}
                </p>
              </div>

              <div className="space-y-2.5">
                {isOracle ? (
                  statusDistribution.map((item) => {
                    const isLocked = item.status === 'LOCKED';
                    const barColor = isLocked ? 'bg-rose-500' : 'bg-emerald-500';
                    return (
                      <div key={item.status} className="space-y-1">
                        <div className="flex items-center justify-between text-xs font-medium">
                          <span className={`flex items-center gap-1.5 ${isLocked ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                            <span className={`w-2 h-2 rounded-full ${isLocked ? 'bg-rose-500' : 'bg-emerald-500'} inline-block`} />
                            {item.status}
                          </span>
                          <span className="text-gray-600 dark:text-gray-300 font-mono text-[11px]">
                            {item.count} ({item.percentage.toFixed(1)}%)
                          </span>
                        </div>
                        <div className="w-full h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${barColor} rounded-full transition-all duration-500`}
                            style={{ width: `${Math.max(item.percentage, 2)}%` }}
                          />
                        </div>
                      </div>
                    );
                  })
                ) : (
                  typeDistribution.map((item) => {
                    let barColor = 'bg-blue-500';
                    if (item.type === 'SQL_LOGIN') barColor = 'bg-blue-500';
                    else if (item.type === 'SQL_USER') barColor = 'bg-blue-500';
                    else if (item.type === 'DATABASE_ROLE') barColor = 'bg-purple-500';
                    else if (item.type.includes('WINDOWS')) barColor = 'bg-amber-500';

                    return (
                      <div key={item.type} className="space-y-1">
                        <div className="flex items-center justify-between text-xs font-medium">
                          <span className="text-gray-700 dark:text-gray-200">{formatTypeName(item.type)}</span>
                          <span className="text-gray-600 dark:text-gray-300 font-mono text-[11px]">{item.count}</span>
                        </div>
                        <div className="w-full h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${barColor} rounded-full transition-all duration-500`}
                            style={{ width: `${Math.max(item.percentage, 2)}%` }}
                          />
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* System vs Custom */}
            <div className="bg-white dark:bg-gray-900/80 rounded-xl border border-gray-200 dark:border-gray-800/80 p-4 shadow-sm">
              <div className="mb-3">
                <h2 className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider flex items-center gap-1.5">
                  <ShieldAlert className="w-3.5 h-3.5 text-amber-500 dark:text-amber-400" />
                  System vs Custom
                </h2>
                <p className="text-[11px] text-gray-500 dark:text-gray-400">Principal origin classification</p>
              </div>

              <div className="space-y-3 pt-2">
                {/* System */}
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-xs font-medium">
                    <span className="text-amber-600 dark:text-amber-400 flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-amber-500 inline-block" />
                      System
                    </span>
                    <span className="text-gray-600 dark:text-gray-300 font-mono text-[11px]">
                      {originDistribution.system} ({originDistribution.systemPercent.toFixed(1)}%)
                    </span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-amber-500 rounded-full transition-all duration-500"
                      style={{ width: `${originDistribution.systemPercent}%` }}
                    />
                  </div>
                </div>

                {/* Custom */}
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-xs font-medium">
                    <span className="text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
                      Custom
                    </span>
                    <span className="text-gray-600 dark:text-gray-300 font-mono text-[11px]">
                      {originDistribution.custom} ({originDistribution.customPercent.toFixed(1)}%)
                    </span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                      style={{ width: `${originDistribution.customPercent}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Details Slide-Over Drawer Modal */}
      {selectedPrincipal && (
        <div className="fixed inset-0 z-50 overflow-hidden bg-black/60 backdrop-blur-sm flex justify-end animate-in fade-in duration-200">
          <div
            className="w-full max-w-lg bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-800 h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drawer Header */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex items-start justify-between bg-gray-50/90 dark:bg-gray-900/90">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-bold text-gray-900 dark:text-white font-mono">
                    {selectedPrincipal.name}
                  </h3>
                  <button
                    onClick={() => handleCopy(selectedPrincipal.name, 'header-name')}
                    className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-white rounded cursor-pointer"
                    title="Copy Name"
                  >
                    {copiedKey === 'header-name' ? (
                      <Check className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400" />
                    ) : (
                      <Copy className="w-3.5 h-3.5" />
                    )}
                  </button>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 mt-2">
                  <span
                    className={`text-[10px] font-bold px-2 py-0.5 rounded border ${selectedPrincipal.scope === 'SERVER'
                      ? 'bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-500/30'
                      : 'bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-500/30'
                      }`}
                  >
                    {selectedPrincipal.scope || 'SERVER'}
                  </span>
                  {selectedPrincipal.account_status ? (
                    <span
                      className={`text-[10px] font-bold px-2 py-0.5 rounded border ${getAccountStatusBadge(
                        selectedPrincipal.account_status,
                        selectedPrincipal.is_disabled
                      )}`}
                    >
                      {selectedPrincipal.account_status}
                    </span>
                  ) : selectedPrincipal.type ? (
                    <span
                      className={`text-[10px] font-bold px-2 py-0.5 rounded border ${getTypeBadgeStyle(
                        selectedPrincipal.type
                      )}`}
                    >
                      {selectedPrincipal.type}
                    </span>
                  ) : null}
                  {selectedPrincipal.profile && (
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-mono border border-gray-200 dark:border-gray-700">
                      Profile: {selectedPrincipal.profile}
                    </span>
                  )}
                  <span
                    className={`text-[10px] font-bold px-2 py-0.5 rounded border ${selectedPrincipal.is_system
                      ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-500/30'
                      : 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/30'
                      }`}
                  >
                    {selectedPrincipal.is_system ? 'System' : 'Custom'}
                  </span>
                  {selectedPrincipal.is_disabled && !selectedPrincipal.account_status && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-red-50 dark:bg-red-500/15 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-500/30">
                      Disabled
                    </span>
                  )}
                </div>
              </div>

              <button
                onClick={() => setSelectedPrincipal(null)}
                className="p-1 rounded-lg bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white border border-gray-200 dark:border-gray-700 cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Drawer Body */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scroll">
              {/* Properties Grid */}
              <div className="bg-gray-50/70 dark:bg-gray-800/40 rounded-xl border border-gray-200 dark:border-gray-800 p-3.5 space-y-3">
                <h4 className="text-[11px] font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">
                  Principal Details
                </h4>

                <div className="grid grid-cols-2 gap-3 text-xs">
                  {selectedPrincipal.account_status && (
                    <div>
                      <span className="text-[10px] text-gray-500 dark:text-gray-400 block">Account Status</span>
                      <span className="font-mono text-gray-900 dark:text-gray-200 font-bold">
                        {selectedPrincipal.account_status}
                      </span>
                    </div>
                  )}

                  {selectedPrincipal.profile && (
                    <div>
                      <span className="text-[10px] text-gray-500 dark:text-gray-400 block">Profile</span>
                      <span className="font-mono text-gray-900 dark:text-gray-200 font-medium">
                        {selectedPrincipal.profile}
                      </span>
                    </div>
                  )}

                  <div>
                    <span className="text-[10px] text-gray-500 dark:text-gray-400 block">Database</span>
                    <span className="font-mono text-gray-900 dark:text-gray-200 font-medium">
                      {selectedPrincipal.database || '-'}
                    </span>
                  </div>

                  <div>
                    <span className="text-[10px] text-gray-500 dark:text-gray-400 block">Login Name</span>
                    <span className="font-mono text-gray-900 dark:text-gray-200 font-medium truncate block">
                      {selectedPrincipal.login_name || '-'}
                    </span>
                  </div>

                  {selectedPrincipal.last_login && (
                    <div>
                      <span className="text-[10px] text-gray-500 dark:text-gray-400 block">Last Login</span>
                      <span className="font-mono text-gray-900 dark:text-gray-200 text-[11px]">
                        {formatTimestamp(selectedPrincipal.last_login)}
                      </span>
                    </div>
                  )}


                  {!isOracle && (
                    <>
                      <div>
                        <span className="text-[10px] text-gray-500 dark:text-gray-400 block">Auth Type</span>
                        <span className="font-mono text-gray-900 dark:text-gray-200 font-medium">
                          {selectedPrincipal.auth_type || 'NONE'}
                        </span>
                      </div>

                      <div>
                        <span className="text-[10px] text-gray-500 dark:text-gray-400 block">Default Database</span>
                        <span className="font-mono text-gray-900 dark:text-gray-200 font-medium">
                          {selectedPrincipal.default_database || '-'}
                        </span>
                      </div>

                      <div className="col-span-2">
                        <span className="text-[10px] text-gray-500 dark:text-gray-400 block">Assigned Roles</span>
                        <span className="font-mono text-gray-900 dark:text-gray-200 font-medium break-all">
                          {selectedPrincipal.roles || 'None'}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
         
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default UsersLoginsView;
